
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { useContentSelection } from '@/contexts/selection/SelectionContext';
import { useAuth } from '@/contexts/AuthContext';
import { Tier1Tracking } from '@/services/analytics/Tier1Tracking';

// Define types for our drag data
export interface DragContentData {
  contentId?: string;
  contentIds?: string[];
  contentTitle?: string;
  contentTitles?: string;
  currentCollectionId?: string;
  isMultiDrag?: boolean;
}

export const useDragAndDrop = () => {
  const [isDragging, setIsDragging] = useState(false);
  const queryClient = useQueryClient();
  const { selectedIds } = useContentSelection();
  const { user } = useAuth();

  // Start dragging a content item
  const handleContentDragStart = (event: React.DragEvent, data: DragContentData) => {
    console.log('Drag started:', data);
    setIsDragging(true);
    
    // Set the drag data as a JSON string
    event.dataTransfer.setData('application/json', JSON.stringify(data));
    
    // Set allowed effects
    event.dataTransfer.effectAllowed = 'move';
    
    // Add a ghost image or use the default
    // This creates a simple placeholder effect
    const ghostElement = document.createElement('div');
    ghostElement.classList.add('bg-white', 'dark:bg-gray-800', 'p-2', 'rounded', 'shadow-md');
    
    if (data.isMultiDrag && data.contentTitles) {
      ghostElement.textContent = data.contentTitles;
    } else {
      ghostElement.textContent = data.contentTitle || 'Content';
    }
    
    ghostElement.style.width = '180px';
    ghostElement.style.opacity = '0.7';
    document.body.appendChild(ghostElement);
    event.dataTransfer.setDragImage(ghostElement, 90, 30);
    
    // Clean up the ghost element after a short delay
    setTimeout(() => {
      document.body.removeChild(ghostElement);
    }, 100);
  };

  // Handle dragging ending
  const handleContentDragEnd = () => {
    console.log('Drag ended');
    setIsDragging(false);
  };

  // Handle dropping on a collection
  const handleDropOnCollection = async (event: React.DragEvent, collectionId: string) => {
    event.preventDefault();
    
    try {
      // Get the drag data
      const jsonData = event.dataTransfer.getData('application/json');
      const data: DragContentData = JSON.parse(jsonData);
      
      // Check if this is a multi-drag operation
      if (data.isMultiDrag && data.contentIds && data.contentIds.length > 0) {
        console.log(`[DRAG&DROP ANALYTICS] Dropping ${data.contentIds.length} items onto collection ${collectionId}`);
        
        // Use batch operation to add all contents to collection
        const { error } = await supabase.rpc('add_content_to_collections', {
          content_id: data.contentIds[0], // We'll handle the batch ourselves
          collection_ids: [collectionId],
        });
        
        // Process each additional item
        if (!error && data.contentIds.length > 1) {
          for (let i = 1; i < data.contentIds.length; i++) {
            await supabase.rpc('add_content_to_collections', {
              content_id: data.contentIds[i],
              collection_ids: [collectionId],
            });
          }
        }
        
        // ANALYTICS: Track each content item being added to collection via drag & drop
        for (const contentId of data.contentIds) {
          try {
            console.log(`[DRAG&DROP ANALYTICS] Tracking content ${contentId} added to collection ${collectionId} via drag & drop`);
            await Tier1Tracking.trackContentOrganized(
              contentId,
              'added_to_collection',
              collectionId,
              'collection',
              user
            );
          } catch (analyticsError) {
            console.error('[DRAG&DROP ANALYTICS] Failed to track collection drag & drop:', analyticsError);
          }
        }
        
        // Invalidate queries to refresh UI
        queryClient.invalidateQueries();
        
        toast.success(`Moved ${data.contentIds.length} items to collection`);
        return;
      }
      
      // Single item case
      if (!data.contentId) {
        console.error('No content ID found in drag data');
        toast.error('Missing content information');
        return;
      }
      
      console.log(`[DRAG&DROP ANALYTICS] Dropping content ${data.contentId} onto collection ${collectionId}`);
      
      // Skip if dragging to same collection
      if (data.currentCollectionId === collectionId) {
        console.log('Content already in this collection');
        toast.info('Content already in this collection');
        return;
      }
      
      // Call function to update database
      const { error } = await supabase.rpc('add_content_to_collections', {
        content_id: data.contentId,
        collection_ids: [collectionId],
      });
      
      if (error) {
        console.error('Error moving content to collection:', error);
        toast.error('Failed to move content to collection');
        return;
      }
      
      // ANALYTICS: Track content added to collection via drag & drop
      try {
        console.log(`[DRAG&DROP ANALYTICS] Tracking content ${data.contentId} added to collection ${collectionId} via drag & drop`);
        await Tier1Tracking.trackContentOrganized(
          data.contentId,
          'added_to_collection',
          collectionId,
          'collection',
          user
        );
      } catch (analyticsError) {
        console.error('[DRAG&DROP ANALYTICS] Failed to track collection drag & drop:', analyticsError);
      }
      
      // IMPROVED: Invalidate all relevant queries to ensure UI updates immediately
      // This ensures that any component showing collection content will refresh
      queryClient.invalidateQueries();
      
      // More specific invalidations for better performance if needed:
      queryClient.invalidateQueries({ queryKey: ['collection'] });
      queryClient.invalidateQueries({ queryKey: ['collectionContent'] });
      queryClient.invalidateQueries({ queryKey: ['contentCount'] });
      queryClient.invalidateQueries({ queryKey: ['relatedContent'] });
      queryClient.invalidateQueries({ queryKey: ['collectionDetails'] });
      
      toast.success(`Moved "${data.contentTitle}" to collection`);
    } catch (error) {
      console.error('Error handling drop on collection:', error);
      toast.error('Failed to process drag and drop operation');
    }
    
    // Reset dragging state
    setIsDragging(false);
  };

  // Handle dropping on a board - extended for multi-drag
  const handleDropOnBoard = async (event: React.DragEvent, boardId: string) => {
    event.preventDefault();
    
    try {
      // Get the drag data
      let data: DragContentData;
      try {
        const jsonData = event.dataTransfer.getData('application/json');
        if (!jsonData) {
          console.error('No valid JSON data found in drag event');
          return;
        }
        data = JSON.parse(jsonData);
      } catch (err) {
        console.error('Error parsing drag data:', err);
        toast.error('Invalid drag data format');
        return;
      }
      
      // Handle multi-drag case
      if (data.isMultiDrag && data.contentIds && data.contentIds.length > 0) {
        console.log(`[DRAG&DROP ANALYTICS] Dropping ${data.contentIds.length} items onto board ${boardId}`);
        
        let successCount = 0;
        let existingCount = 0;
        
        // Process each content ID sequentially
        for (const contentId of data.contentIds) {
          // Check if content is already on this board
          const { data: existingRelation, error: checkError } = await supabase
            .from('content_boards')
            .select('*')
            .eq('content_id', contentId)
            .eq('board_id', boardId);
            
          if (checkError) {
            console.error('Error checking existing board relation:', checkError);
            continue;
          }
          
          // Skip if already on board
          if (existingRelation && existingRelation.length > 0) {
            existingCount++;
            continue;
          }
          
          // Add content to board
          const { error } = await supabase
            .from('content_boards')
            .upsert({
              content_id: contentId,
              board_id: boardId,
              position_x: 0, // Default position
              position_y: 0  // Default position
            });
          
          if (error) {
            console.error(`Error adding content ${contentId} to board:`, error);
            continue;
          }
          
          // ANALYTICS: Track content added to board via drag & drop
          try {
            console.log(`[DRAG&DROP ANALYTICS] Tracking content ${contentId} added to board ${boardId} via drag & drop`);
            await Tier1Tracking.trackContentOrganized(
              contentId,
              'added_to_board',
              boardId,
              'board',
              user
            );
          } catch (analyticsError) {
            console.error('[DRAG&DROP ANALYTICS] Failed to track board drag & drop:', analyticsError);
          }
          
          successCount++;
        }
        
        // Invalidate queries to update UI
        queryClient.invalidateQueries({ queryKey: ['relatedContent', boardId, 'board'] });
        queryClient.invalidateQueries({ queryKey: ['boardContent'] });
        queryClient.invalidateQueries({ queryKey: ['board', boardId] });
        
        // Show appropriate toast message
        if (successCount > 0) {
          toast.success(`Added ${successCount} items to board`);
        }
        
        if (existingCount > 0) {
          toast.info(`${existingCount} items were already on this board`);
        }
        
        return;
      }
      
      // Single item case - unchanged
      if (!data || !data.contentId) {
        console.error('Invalid content data in drag operation');
        toast.error('Failed to read content information');
        return;
      }
      
      console.log(`[DRAG&DROP ANALYTICS] Dropping content ${data.contentId} onto board ${boardId}`);
      
      // Check if content is already on this board with improved logging
      const { data: existingRelation, error: checkError } = await supabase
        .from('content_boards')
        .select('*')
        .eq('content_id', data.contentId)
        .eq('board_id', boardId);
        
      if (checkError) {
        console.error('Error checking existing board relation:', checkError);
        toast.error('Failed to check if content is already on board');
        return;
      }
      
      // Fixed: Check if array has items instead of using single()
      if (existingRelation && existingRelation.length > 0) {
        console.log('Content already on this board. Relation data:', existingRelation);
        toast.info('Content already on this board');
        
        // Force refresh the board content to ensure it displays
        queryClient.invalidateQueries({ queryKey: ['relatedContent', boardId, 'board'] });
        queryClient.invalidateQueries({ queryKey: ['board', boardId] });
        queryClient.invalidateQueries({ queryKey: ['boardContent'] });
        return;
      }
      
      // Add content to board
      const { error } = await supabase
        .from('content_boards')
        .upsert({
          content_id: data.contentId,
          board_id: boardId,
          position_x: 0, // Default position
          position_y: 0  // Default position
        });
      
      if (error) {
        console.error('Error adding content to board:', error);
        toast.error('Failed to add content to board');
        return;
      }
      
      // ANALYTICS: Track content added to board via drag & drop
      try {
        console.log(`[DRAG&DROP ANALYTICS] Tracking content ${data.contentId} added to board ${boardId} via drag & drop`);
        await Tier1Tracking.trackContentOrganized(
          data.contentId,
          'added_to_board',
          boardId,
          'board',
          user
        );
      } catch (analyticsError) {
        console.error('[DRAG&DROP ANALYTICS] Failed to track board drag & drop:', analyticsError);
      }
      
      // Invalidate queries to update UI
      queryClient.invalidateQueries({ queryKey: ['relatedContent', boardId, 'board'] });
      queryClient.invalidateQueries({ queryKey: ['boardContent'] });
      queryClient.invalidateQueries({ queryKey: ['board', boardId] });
      
      toast.success(`Added "${data.contentTitle}" to board`);
    } catch (error) {
      console.error('Error handling drop on board:', error);
      toast.error('Failed to process drag and drop operation');
    }
    
    // Reset dragging state
    setIsDragging(false);
  };

  // Handle dropping on a tag - extended for multi-drag
  const handleDropOnTag = async (event: React.DragEvent, tagId: string, tagName: string) => {
    event.preventDefault();
    
    try {
      // Get the drag data
      const jsonData = event.dataTransfer.getData('application/json');
      const data: DragContentData = JSON.parse(jsonData);
      
      // Handle multi-drag case
      if (data.isMultiDrag && data.contentIds && data.contentIds.length > 0) {
        console.log(`[DRAG&DROP ANALYTICS] Dropping ${data.contentIds.length} items onto tag ${tagId}`);
        
        let successCount = 0;
        let existingCount = 0;
        
        // Process each content ID sequentially
        for (const contentId of data.contentIds) {
          // Add tag to content
          const { error } = await supabase
            .from('content_tags')
            .insert({ content_id: contentId, tag_id: tagId });
          
          if (error) {
            // Handle case where tag is already applied
            if (error.code === '23505') { // Unique constraint violation
              existingCount++;
              continue;
            }
            
            console.error(`Error adding tag to content ${contentId}:`, error);
            continue;
          }
          
          // ANALYTICS: Track content tagged via drag & drop
          try {
            console.log(`[DRAG&DROP ANALYTICS] Tracking content ${contentId} tagged with ${tagId} via drag & drop`);
            await Tier1Tracking.trackContentOrganized(
              contentId,
              'tagged',
              tagId,
              'tag',
              user
            );
          } catch (analyticsError) {
            console.error('[DRAG&DROP ANALYTICS] Failed to track tag drag & drop:', analyticsError);
          }
          
          successCount++;
        }
        
        // Invalidate all relevant queries for immediate UI updates
        queryClient.invalidateQueries({ queryKey: ['contentTags'] });
        queryClient.invalidateQueries({ queryKey: ['tagContent'] });
        
        // Show appropriate toast messages
        if (successCount > 0) {
          toast.success(`Added tag "${tagName}" to ${successCount} items`);
        }
        
        if (existingCount > 0) {
          toast.info(`Tag "${tagName}" was already applied to ${existingCount} items`);
        }
        
        return;
      }
      
      // Single item case
      console.log(`[DRAG&DROP ANALYTICS] Dropping content ${data.contentId} onto tag ${tagId}`);
      
      // Add tag to content
      const { error } = await supabase
        .from('content_tags')
        .insert({ content_id: data.contentId, tag_id: tagId })
        .single();
      
      if (error) {
        // Handle case where tag is already applied
        if (error.code === '23505') { // Unique constraint violation
          console.log('Tag already applied to this content');
          toast.info(`Tag "${tagName}" already applied to this content`);
          return;
        }
        
        console.error('Error adding tag to content:', error);
        toast.error('Failed to add tag to content');
        return;
      }
      
      // ANALYTICS: Track content tagged via drag & drop
      try {
        console.log(`[DRAG&DROP ANALYTICS] Tracking content ${data.contentId} tagged with ${tagId} via drag & drop`);
        await Tier1Tracking.trackContentOrganized(
          data.contentId,
          'tagged',
          tagId,
          'tag',
          user
        );
      } catch (analyticsError) {
        console.error('[DRAG&DROP ANALYTICS] Failed to track tag drag & drop:', analyticsError);
      }
      
      // IMPROVED: Invalidate all relevant queries for immediate UI updates
      queryClient.invalidateQueries({ queryKey: ['contentTags'] });
      queryClient.invalidateQueries({ queryKey: ['tagContent'] });
      
      toast.success(`Added tag "${tagName}" to "${data.contentTitle}"`);
    } catch (error) {
      console.error('Error handling drop on tag:', error);
      toast.error('Failed to process drag and drop operation');
    }
    
    // Reset dragging state
    setIsDragging(false);
  };

  // Handle drag over for droppable targets - consistent for both collections and tags
  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  return {
    isDragging,
    selectedIds,
    handleContentDragStart,
    handleContentDragEnd,
    handleDropOnCollection,
    handleDropOnBoard,
    handleDropOnTag,
    handleDragOver
  };
};
